import type { CSSProperties } from "react";
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useCan } from "../hooks/useCan";

/**
 * Messaging: everything that texts clients: campaigns, surveys, the
 * automatic SMS, the gifts they offer, and the PDF pages they link to. Pathless, so the
 * tabs wrap `/campaigns` and `/surveys` without moving either URL — survey
 * links already bookmarked, and the ones in the command palette, keep working.
 */
export const Route = createFileRoute("/_auth/_messaging")({
  component: MessagingLayout,
});

function MessagingLayout() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onSurveys = pathname.startsWith("/surveys");
  const onAuto = pathname.startsWith("/auto-messages");
  const onGifts = pathname.startsWith("/gifts");
  const onOffers = pathname.startsWith("/offers");
  const onCampaigns = !onSurveys && !onAuto && !onGifts && !onOffers;
  const canOffers = useCan("view_promos");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={tabs}>
        <Link to="/campaigns" style={{ ...tab, ...(onCampaigns ? tabOn : {}) }}>
          {t("messaging.tab.campaigns")}
        </Link>
        <Link to="/surveys" style={{ ...tab, ...(onSurveys ? tabOn : {}) }}>
          {t("messaging.tab.surveys")}
        </Link>
        <Link to="/auto-messages" style={{ ...tab, ...(onAuto ? tabOn : {}) }}>
          {t("messaging.tab.automatic")}
        </Link>
        <Link to="/gifts" style={{ ...tab, ...(onGifts ? tabOn : {}) }}>
          {t("messaging.tab.gifts")}
        </Link>
        {canOffers && (
          <Link to="/offers" style={{ ...tab, ...(onOffers ? tabOn : {}) }}>
            {t("messaging.tab.offers")}
          </Link>
        )}
      </div>
      <Outlet />
    </div>
  );
}

const tabs: CSSProperties = {
  display: "inline-flex",
  gap: 6,
  padding: 4,
  borderRadius: 999,
  background: "var(--bg-inset)",
  alignSelf: "flex-start",
  maxWidth: "100%",
  overflowX: "auto",
  fontSize: 13,
};
const tab: CSSProperties = {
  padding: "5px 16px",
  borderRadius: 999,
  textDecoration: "none",
  color: "var(--fg-mute)",
  border: "1px solid transparent",
  whiteSpace: "nowrap",
};
const tabOn: CSSProperties = {
  background: "var(--bg)",
  color: "var(--fg)",
  border: "1px solid var(--line-soft)",
};
